import { useCallback, useEffect, useRef } from 'react'
import { useHistory } from 'react-router-dom'
import { Box } from 'theme-ui'
import { Nav } from 'components'
import { useAuth, useConfigUtils } from 'providers'
import { useCreate } from 'hooks/userHooks'
import { CreateStudentForm } from 'components/UserForm/CreateStudentForm'
import { StudentFormContentLoader } from 'components/UserForm/StudentFormContentLoader'
import { StudentCreateFormValues } from 'types'
import getLocalized from 'i18n'
import * as access from 'types/access'

const localized = getLocalized()

export function Create() {
  useEffect(() => {
    document.title = localized.documentTitles.adminDashboard
  }, [])

  const { profile, logout, hasAccess } = useAuth()
  const history = useHistory()
  const utils = useConfigUtils()
  const [state, create] = useCreate()
  const isCustomerSupportUser = profile.groups.includes('SupportGroup')

  async function handleLogoutClick() {
    await logout(profile.email)
    history.push({
      pathname: '/',
      state: undefined,
    })
  }
  const navigateToDashboard = useCallback(() => {
    history.push({ pathname: '/', state: null })
  }, [history])
  const hasNavigated = useRef<boolean>(false)
  useEffect(() => {
    if (state.matches('success') && !hasNavigated.current) {
      hasNavigated.current = true
      navigateToDashboard()
    }
  }, [state, navigateToDashboard])

  function handleFormSubmit(values: StudentCreateFormValues) {
    const formData = {
      teacher: profile.id,
      ...values,
    }
    create(formData)
  }

  const isTeacherTraining = !hasAccess(
    access.Groups.DASHBOARD,
    access.Dashboard.CREATE_USERS
  )

  const canCreateStudents =
    hasAccess(access.Groups.DASHBOARD, access.Dashboard.CREATE_TEST_USERS) ||
    hasAccess(access.Groups.DASHBOARD, access.Dashboard.CREATE_USERS)

  const canCreateTeachers = profile?.groups?.includes('SupportGroup')

  const canViewHelpCenter = hasAccess(
    access.Groups.DASHBOARD,
    access.Dashboard.VIEW_HELP_CENTER
  )
  const canViewDropdownDate = hasAccess(
    access.Groups.DASHBOARD,
    access.Dashboard.NO_DATE_OF_INSTRUCTION_RESTRICTION
  )

  return (
    <Box
      data-cy="create-page"
      sx={{
        bg: 'whitesmoke',
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
      }}
    >
      <Nav
        profile={profile}
        onLogoutClick={handleLogoutClick}
        access={{
          createStudent: canCreateStudents,
          createTeacher: canCreateTeachers,
          viewHelpCenter: canViewHelpCenter,
        }}
      />
      <Box
        data-cy="student-create"
        sx={{
          width: '100vw',
          flexDirection: 'column',
          flex: '1 0 auto',
          alignItems: 'center',
          bg: 'white',
          pt: 68,
          position: 'relative',
        }}
      >
        {utils ? (
          <CreateStudentForm
            pending={state.matches('syncing')}
            disabled={state.matches('syncing')}
            configUtils={utils}
            responseError={
              state.matches('failed') ? state.context.error : undefined
            }
            isSupport={isCustomerSupportUser}
            isTeacherTraining={isTeacherTraining}
            onFormSubmit={handleFormSubmit}
            onFormCancel={navigateToDashboard}
            access={{
              viewDropdownDate: canViewDropdownDate,
            }}
          />
        ) : (
          <Box variant="userProfile.form">
            <StudentFormContentLoader />
          </Box>
        )}
      </Box>
    </Box>
  )
}
